<script setup>
// ESLintルールは必要な箇所のみ無効化する
/**
 * BannerDock - 画面下部に固定表示されるバナードックコンポーネント
 * 
 * 使用例:
 * <BannerDock 
 *   :banners="bannerItems" 
 *   :max-mobile-items="4" 
 *   :max-desktop-items="10"
 *   :dock-width="80"
 *   :current-category="selectedCategory"
 *   @banner-click="handleBannerClick"
 *   @top50-click="handleTop50Click"
 *   @category-click="handleCategoryClick"
 *   @category-select="handleCategorySelect"
 * />
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import BannerAd from './BannerAd.vue';
import { useDeviceDetection, truncateText } from '@/services/uiUtils';

// イベント定義
// eslint-disable-next-line no-undef
const emit = defineEmits(['banner-click', 'top50-click', 'category-click', 'category-select']);

// eslint-disable-next-line no-undef
const props = defineProps({
  /**
   * 現在選択されているカテゴリ名
   */
  currentCategory: {
    type: String,
    default: ''
  },
  
  /**
   * カテゴリ表示名のマッピング
   */
  categoryNames: {
    type: Object,
    default: () => ({
      toys: 'おもちゃ',
      games: 'ゲーム',
      books: '本'
    })
  },
  /**
   * バナーアイテムの配列
   */
  banners: {
    type: Array,
    default: () => []
  },
  
  /**
   * ドックの幅 (画面幅に対する割合 %)
   */
  dockWidth: {
    type: Number,
    default: 80
  },
  
  /**
   * モバイル表示時の最大アイテム数
   */
  maxMobileItems: {
    type: Number,
    default: 4
  },
  
  /**
   * デスクトップ表示時の最大アイテム数
   */
  maxDesktopItems: {
    type: Number,
    default: 50
  },
  
  /**
   * モバイル判定の閾値 (px)
   */
  mobileBreakpoint: {
    type: Number,
    default: 740
  }
});

// デバイス状態の管理
const { isMobile } = useDeviceDetection(props.mobileBreakpoint);

// カテゴリ選択プルダウンの表示状態
const showCategoryDropdown = ref(false);

// 画面幅に収まるバナー数を計算するための参照
const availableSpace = ref(window.innerWidth);
const calculatedItemCount = ref(0);

// tiles-containerの幅を同期するための参照
const tilesContainerWidth = ref(0);

// 商品名を制限する関数
const truncateProductName = (name) => truncateText(name, 7);

// タイルコンテナの幅を取得する関数
const updateTilesContainerWidth = () => {
  // タイルコンテナ要素を取得
  const tilesContainer = document.querySelector('.tiles-container');
  if (tilesContainer) {
    // 幅を取得し、前回の値と異なる場合のみ更新（デバッグ情報を出力）
    const newWidth = tilesContainer.offsetWidth;
    if (tilesContainerWidth.value !== newWidth) {
      // console.log(`タイルコンテナ幅の更新: ${tilesContainerWidth.value}px → ${newWidth}px`);
      tilesContainerWidth.value = newWidth;
      // 幅が変わったら表示アイテム数も再計算
      updateVisibleItems();
    }
  } else {
    console.log('タイルコンテナが見つかりません');
  }
};

// 画面幅に基づいて表示可能なアイテム数を計算
const calculateMaxVisibleItems = () => {
  if (isMobile.value) return props.maxMobileItems;
  
  // タイルコンテナの幅が取得できている場合
  if (tilesContainerWidth.value > 0) {
    // PCでのアイコンのサイズ定義（template内のsizeプロパティと一致させる）
    let iconSize = isMobile.value ? 70 : 100; // BannerAdのsizeプロパティと同じ値を使用
    let marginBetweenIcons = 8; // バナーアイテムのマージン左右合計(4px + 4px)
    
    // ドックの左右パディング合計
    const dockPadding = 16; // .banner-dock の左右パディング(8px + 8px)
    
    // タイルコンテナの幅から、実際に使用できる幅を計算
    const availableWidth = tilesContainerWidth.value - dockPadding;
    
    // 1つのアイテムが占める全体幅（アイコン + マージン）
    const itemTotalWidth = iconSize + marginBetweenIcons;
    
    // 利用可能な幅に入るアイテム数を計算
    // 最後のアイテムはマージンが不要なので、少し余裕ができる
    const maxItemsInContainer = Math.floor((availableWidth + marginBetweenIcons) / itemTotalWidth);
    
    // デバッグ情報
    // console.log(`タイルコンテナ計算: 幅=${tilesContainerWidth.value}px, 使用可能幅=${availableWidth}px, アイコンサイズ=${iconSize}px, 合計幅=${itemTotalWidth}px, 表示可能数=${maxItemsInContainer}`);
    
    // 少なくとも1つは表示
    return Math.max(1, Math.min(maxItemsInContainer, props.banners.length, props.maxDesktopItems));
  }
  
  // 画面幅から計算（フォールバック）
  const viewportWidth = window.innerWidth;
  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
  const actualWidth = viewportWidth - scrollBarWidth;
  
  // 画面サイズによって異なるアイコンサイズとコンテナ幅を設定
  let iconSize = isMobile.value ? 70 : 100;
  let containerWidthPercent = 95;
  
  if (actualWidth >= 900) {
    // 大型ディスプレイ
    containerWidthPercent = 90; // コンテナ幅は画面の90%
  } else if (actualWidth >= 768) {
    // PC
    containerWidthPercent = 90;
  }
  
  // 利用可能な幅を計算
  const usableWidth = actualWidth * (containerWidthPercent / 100);
  
  // アイテム間のマージンとパディング
  const marginBetweenIcons = 8; // 左右のマージン合計
  const dockPadding = 16; // 左右のパディング合計
  
  // 1アイテムの全体幅
  const itemTotalWidth = iconSize + marginBetweenIcons;
  
  // 表示可能なアイテム数を計算（最後のアイテムはマージンが不要なので調整）
  const maxItemsInViewport = Math.floor((usableWidth - dockPadding + marginBetweenIcons) / itemTotalWidth);
  
  // 最小1つは表示し、バナー総数と表示上限と比較
  const result = Math.max(1, Math.min(maxItemsInViewport, props.banners.length, props.maxDesktopItems));
  
  // console.log(`画面幅に基づく計算: 画面幅=${actualWidth}px, 利用可能幅=${usableWidth}px, アイコンサイズ=${iconSize}px, アイテム幅=${itemTotalWidth}px, 表示可能数=${result}`);
  
  return result;
};

// ウィンドウサイズが変更されたときにアイテム数を再計算
const updateVisibleItems = () => {
  availableSpace.value = window.innerWidth;
  // 初期値として少なくともモバイルなら最大モバイルアイテム数、PCなら最低5つは表示する
  const minItems = isMobile.value ? props.maxMobileItems : 5;
  const calculated = calculateMaxVisibleItems();
  calculatedItemCount.value = Math.max(minItems, calculated);
};

// 表示するバナーを制限
const limitedBanners = computed(() => {
  // 最低表示数の保証: モバイルなら最大モバイルアイテム数、PCなら最低5つ
  const minItems = isMobile.value ? props.maxMobileItems : 5;
  const maxItems = calculatedItemCount.value || minItems;
  return props.banners.slice(0, maxItems);
});

// ドックのスタイル計算
const dockStyles = computed(() => {
  // アイコンの数に基づいて最適な幅を計算
  let calculatedWidth = props.dockWidth;
  
  // tiles-containerの幅と同期する
  if (tilesContainerWidth.value > 0) {
    // タイルコンテナの幅がある場合はそれを使用（ピクセル指定）
    return {
      width: `${tilesContainerWidth.value}px`,
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'center',
      boxSizing: 'border-box'
    };
  }
  
  if (!isMobile.value) {
    // PCでのアイコンサイズとマージンを調整
    const iconSize = 80; // PCでのアイコンサイズ
    const marginBetweenIcons = 12; // 左右のマージンの合計
    const dockPadding = 16; // 左右のパディングの合計（8px + 8px）
    
    // 実際に表示するアイコンの数（計算済みの表示アイコン数を使用）
    const maxVisible = calculatedItemCount.value || 
                      Math.min(limitedBanners.value.length, props.maxDesktopItems);
    const visibleIcons = Math.max(maxVisible, 4); // 少なくとも4つ分のスペースを確保
    
    // スクロールバーを考慮した実際の内部コンテンツ幅を取得
    const viewportWidth = window.innerWidth;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const actualContentWidth = viewportWidth - scrollBarWidth;
    
    // 必要な幅をピクセルで計算
    const requiredWidth = (iconSize * visibleIcons) + (marginBetweenIcons * (visibleIcons - 1)) + dockPadding;
    
    // iPad miniなどのタブレットサイズの場合は特別な処理
    if (actualContentWidth <= 900) {
      // タブレット表示の場合は画面幅の90%を使用
      calculatedWidth = 90;
    } else {
      // 通常のPC表示の場合は必要な幅を計算
      calculatedWidth = Math.min(Math.ceil((requiredWidth / actualContentWidth) * 100), 95);
      // 少なくとも60%以上の幅を確保
      calculatedWidth = Math.max(calculatedWidth, 60);
    }
  }
  
  // 中央配置のスタイルを返す（タイルコンテナの幅が取得できなかった場合のフォールバック）
  return {
    width: isMobile.value ? '100%' : `${calculatedWidth}%`,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box'
  };
});

// リンクの二重開封を防止するためのフラグ
let isLinkProcessing = false;
// デバッグ用：クリックカウンタ
let clickCounter = 0;

// 未使用関数のESLintエラー対策
// eslint-disable-next-line no-unused-vars
function bannerClickEvent(eventData) {
  // この関数は使用されていないため、将来的に削除してください
  console.log('未使用の関数です', eventData);
}

// バナーがクリックされたときのハンドラ
function handleBannerClick(data) {
  // クリックカウンタを増やす
  clickCounter++;
  const currentClick = clickCounter;
  console.log(`[BannerDock] バナークリック処理 #${currentClick} - データ:`, data);
  
  // データの有効性を確認
  if (!data || !data.url) {
    console.error(`[BannerDock] #${currentClick} - 無効なデータです:`, data);
    return;
  }

  // URLの確認
  console.log(`[BannerDock] #${currentClick} - URL: ${data.url}, 処理中フラグ: ${isLinkProcessing}`);
  
  // 既にリンク処理中なら何もしない（二重開封防止）
  if (isLinkProcessing) {
    console.log(`[BannerDock] #${currentClick} - 既に処理中のため無視します`);
    return;
  }

  // URLが存在する場合はリンクを開く
  if (data.url && data.url !== '#') {
    // フラグを設定してリンクの二重開封を防止
    isLinkProcessing = true;
    console.log(`[BannerDock] #${currentClick} - リンク処理を開始します: ${data.url}`);

    // この部分はApp.vueに処理を委譲するため、コメントアウト
    /*
    if (data.openInNewTab !== false) {
      // 新規タブで開く処理
      try {
        console.log(`[BannerDock] #${currentClick} - 新規タブでリンクを開こうとしています`);
        const newWindow = window.open();
        if (newWindow) {
          console.log(`[BannerDock] #${currentClick} - 新規ウィンドウが作成されました`);
          newWindow.opener = null; // refererを隠す
          newWindow.location = data.url;
        } else {
          console.warn(`[BannerDock] #${currentClick} - 新規ウィンドウの作成に失敗しました（ポップアップがブロックされた可能性があります）`);
        }
      } catch (e) {
        console.error(`[BannerDock] #${currentClick} - リンクを開く際にエラーが発生しました`, e);
      }
    } else {
      // 同一タブで開く
      console.log(`[BannerDock] #${currentClick} - 同一タブでリンクを開きます: ${data.url}`);
      window.location.href = data.url;
    }
    */

    // 処理完了後、一定時間経過したらフラグをリセット
    setTimeout(() => {
      console.log(`[BannerDock] #${currentClick} - リンク処理フラグをリセットします`);
      isLinkProcessing = false;
    }, 1000); // 1秒間は同じリンクの開封を防止
  }

  // 親コンポーネントにイベントを伝播（データと現在時刻を含む）
  const eventData = {
    ...data,
    timestamp: new Date().toISOString()
  };
  console.log(`[BannerDock] #${currentClick} - 親コンポーネントにイベントを伝播します:`, eventData);
  emit('banner-click', eventData);
}

// TOP50ボタンがクリックされたときのハンドラ
function handleTop50Click() {
  emit('top50-click');
}

// カテゴリボタンがクリックされたときのハンドラ
function toggleCategoryDropdown() {
  showCategoryDropdown.value = !showCategoryDropdown.value;
  emit('category-click', props.currentCategory);
}

// カテゴリが選択されたときのハンドラ
function selectCategory(categoryId) {
  showCategoryDropdown.value = false;
  emit('category-select', categoryId);
}

// 現在のカテゴリ表示名を取得
const currentCategoryName = computed(() => {
  return props.categoryNames[props.currentCategory] || props.currentCategory || 'カテゴリ';
});

// ライフサイクルフック
onMounted(() => {
  // DOMが完全に描画された後にタイルコンテナの幅を取得するため、少し遅延させる
  setTimeout(() => {
    updateTilesContainerWidth();
    updateVisibleItems();
    // console.log('初回レンダリング後のタイル幅更新を実行');
  }, 300);
  
  // リサイズイベントリスナー
  const handleResize = () => {
    updateVisibleItems();
    updateTilesContainerWidth();
  };
  
  window.addEventListener('resize', handleResize);
  
  // タイルコンテナの変更を監視するためのMutationObserverを設定
  const observer = new MutationObserver(() => {
    updateTilesContainerWidth();
  });
  
  // タイルコンテナ要素を監視
  const tilesContainer = document.querySelector('.tiles-container');
  if (tilesContainer) {
    observer.observe(tilesContainer, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  // カテゴリドロップダウンを閉じるためのクリックイベントリスナー
  const handleClickOutside = (event) => {
    if (showCategoryDropdown.value && !event.target.closest('.category-button')) {
      showCategoryDropdown.value = false;
    }
  };
  
  window.addEventListener('click', handleClickOutside);
  
  // 定期的にタイルコンテナの幅を確認（画面変化に対応）
  // 開始時は短い間隔で確認し、その後長い間隔に切り替える
  let checkCount = 0;
  let regularInterval = null;
  
  const initialInterval = setInterval(() => {
    updateTilesContainerWidth();
    checkCount++;
    if (checkCount >= 5) {
      clearInterval(initialInterval);
      // 初期チェック後は1秒間隔で継続的にチェック
      regularInterval = setInterval(updateTilesContainerWidth, 1000);
    }
  }, 200);
  
  // コンポーネントのアンマウント時にクリーンアップ
  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('click', handleClickOutside);
    observer.disconnect();
    clearInterval(initialInterval);
    if (regularInterval) clearInterval(regularInterval);
  });
});

// タイルの⋮アイコンにクリックイベントを追加
function handleEllipsisClick(event, banner) {
  event.stopPropagation(); // 親要素へのイベント伝播を防止
  console.log(`[BannerDock] タイルの⋮がクリックされました:`, banner);
  // 必要に応じて追加の処理をここに記述
}
</script>

<template>
  <div class="banner-dock-container" :class="{ 'is-mobile': isMobile, 'dropdown-active': showCategoryDropdown }">
    <!-- ボタンコンテナ -->
    <div class="dock-buttons-container">
      <!-- TOP50ボタン -->
      <div class="dock-button" @click.passive="handleTop50Click">
        <div class="button-icon">TOP20</div>
      </div>
      
      <!-- カテゴリボタン -->
      <div class="dock-button category-button" @click.stop.passive="toggleCategoryDropdown">
        <div class="button-icon">{{ currentCategoryName }}</div>
        
        <!-- カテゴリ選択プルダウン -->
        <div v-if="showCategoryDropdown" class="category-dropdown">
          <div 
            v-for="(name, id) in props.categoryNames" 
            :key="id" 
            class="category-item"
            @click.stop.passive="selectCategory(id)"
          >
            {{ name }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- メインのバナードック -->
    <div class="banner-dock" :style="dockStyles">
      <div v-for="(banner, index) in limitedBanners" :key="index" class="banner-item">
        <BannerAd 
          :productName="truncateProductName(banner.productName)"
          :discount="banner.discount"
          :backgroundImage="banner.backgroundImage"
          :backgroundColor="banner.backgroundColor"
          :size="isMobile ? 70 : 100"
          :borderRadius="isMobile ? 16 : 20"
          :url="banner.url"
          :openInNewTab="true"
          @click="(data) => handleBannerClick(data || banner)"
        />
        <!-- ⋮アイコン -->
        <div class="ellipsis-icon" @click="(event) => handleEllipsisClick(event, banner)">
          ⋮
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ボタンコンテナのスタイル */
.dock-buttons-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 5px;
  position: relative;
  z-index: 1000;
}

/* ドックボタンのスタイル */
.dock-button {
  width: 60px;
  height: 20px;
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.dock-button:hover {
  transform: scale(1.05);
  background: rgba(0, 0, 0, 0.6);
}

/* プルダウンが表示されている時はカテゴリボタンのホバー効果を無効化 */
.dropdown-active .category-button:hover {
  transform: none !important;
}

.dock-button:active {
  transform: scale(0.95);
}

.button-icon {
  font-size: 8px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  letter-spacing: 0.5px;
}

/* カテゴリボタンのスタイル */
.category-button {
  position: relative;
}

/* カテゴリドロップダウンのスタイル */
.category-dropdown {
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  z-index: 2000;
  transform: translateX(-50%) !important;
  transition: none !important;
}

.category-item {
  padding: 12px 16px;
  font-size: 12px;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: center;
}

.category-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: none;
}

/* モバイル向けのボタンスタイル調整 */
.is-mobile .dock-button {
  width: 60px;
  height: 25px;
  border-radius: 12.5px;
}

.is-mobile .category-dropdown {
  width: 200px;
}

/* スマートフォン表示時のボタン配置調整 */
.is-mobile .dock-buttons-container {
  margin-bottom: 0;
  padding-bottom: 5px;
  position: fixed;
  bottom: 100px;
  left: 0;
  width: 100%;
  z-index: 1500;
}

.banner-dock-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  z-index: 999;
  box-sizing: border-box;
}

.banner-dock {
  display: flex;
  justify-content: flex-start; /* 左寄せに変更してタイルを最大限表示できるようにする */
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(3px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 
    0 4px 30px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.3);
  overflow-x: auto; /* スクロール可能にして表示しきれないタイルも見られるようにする */
  scrollbar-width: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin: 0 auto;
  max-width: 95%;
  box-sizing: border-box;
  min-height: 96px; /* アイコンサイズ+パディングに合わせる */
}

/* PC版のホバーエフェクト（Macのdock風） */
.banner-dock-container:not(.is-mobile):not(.dropdown-active) .banner-item:hover {
  transform: scale(1.1) translateY(-8px);
}

/* ドロップダウンが表示されている時はホバー効果を無効化 */
.banner-dock-container.dropdown-active .banner-item:hover {
  transform: none;
}

.banner-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 4px; /* マージンを6pxから4pxに縮小して、より多くのアイテムを表示 */
  transition: transform 0.2s ease;
}

/* モバイル向けスタイル */
.is-mobile .banner-dock {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  border-radius: 16px 16px 0 0;
  border-bottom: none;
  padding: 8px 0;
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.1);
}

/* メディアクエリを追加して明示的にデバイス幅を考慮する */
@media (max-width: 767px) {
  .dock-buttons-container {
    margin-bottom: 40px;
    padding-bottom: 8px;
  }
  
  .banner-dock {
    border-radius: 10px;
    padding: 6px 8px; /* 左右のパディングを均等に8pxに変更 */
  }
}

/* iPadなど中間サイズのデバイス向け */
@media (min-width: 768px) and (max-width: 900px) {
  .banner-dock {
    width: 90%;
    max-width: 95%;
  }
  
  .dock-buttons-container {
    margin-bottom: 15px;
  }
}

/* iPad mini縦画面専用の調整（744px付近） */
@media (min-width: 740px) and (max-width: 768px) and (orientation: portrait) {
  .dock-buttons-container {
    margin-bottom: 20px;
    position: relative;
    bottom: -5px;
  }
}

/* ⋮アイコンのスタイルを調整 */
.ellipsis-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 16px;
  cursor: pointer;
  z-index: 10; /* 他の要素より前面に表示 */
}

.ellipsis-icon:hover {
  color: rgba(0, 0, 0, 0.7);
}
</style>
